// Shared cache-tag + version constants for the never-expire, renew-on-change system.
// Server: unstable_cache(..., { revalidate: false, tags }) — lives forever until
// the Strapi webhook calls revalidateTag(). Client: TanStack Query persisted to
// IndexedDB with staleTime: Infinity — lives across revisits / tomorrow until
// the version gate sees a new server version and invalidates in background.
export const STRAPI_TAG = "strapi";

export const STRAPI_TAGS = {
  all: ["strapi"],
  menus: ["strapi", "strapi:menus"],
  contents: ["strapi", "strapi:contents"],
  footer: ["strapi", "strapi:footer"],
  vxi: ["strapi", "strapi:vxi-contents"],
  hubs: ["strapi", "strapi:cxa-hubs"],
  pages: ["strapi", "strapi:pages"],
  misc: ["strapi", "strapi:misc"],
};

// Map a Strapi webhook `model` string to the tags that must be busted.
// Unknown models fall back to busting everything (safe default).
// NOTE: `leaders` (`layout.leaders` / `leadersList[].profile`) and
// `floor elevator` (`layout.features-elevator` / `floorPanel[].images`) are
// nested inside the page payload (`fetchByUrl` -> tags `strapi:pages`), not
// top-level collections. Their webhooks arrive as child/component models
// (e.g. `api::leader.leader`, `api::floor-panel.floor-panel`, `layout.leaders`)
// so they MUST bust `strapi:pages` explicitly — falling back to just
// `["strapi"]` is technically enough today, but fragile and hides intent.
export function tagsForModel(model) {
  const m = String(model || "").toLowerCase();
  if (!m) return STRAPI_TAGS.all;
  if (m.includes("menu")) return [...STRAPI_TAGS.menus, ...STRAPI_TAGS.all];
  if (m.includes("vxi-content")) return [...STRAPI_TAGS.vxi, ...STRAPI_TAGS.pages, ...STRAPI_TAGS.all];
  if (m.includes("cxa-hub")) return [...STRAPI_TAGS.hubs, ...STRAPI_TAGS.pages, ...STRAPI_TAGS.all];
  // Nested page sections: leaders + floor elevator live inside Content.section.
  // Explicit branches so a child-model webhook always busts the page cache.
  if (m.includes("leader")) return [...STRAPI_TAGS.contents, ...STRAPI_TAGS.pages, ...STRAPI_TAGS.all];
  if (m.includes("floor") || m.includes("elevator") || m.includes("features-elevator"))
    return [...STRAPI_TAGS.contents, ...STRAPI_TAGS.pages, ...STRAPI_TAGS.all];
  // Any other layout/specials component edit only matters via its parent page.
  if (m.includes("layout.") || m.includes("component") || m.includes("specials."))
    return [...STRAPI_TAGS.contents, ...STRAPI_TAGS.pages, ...STRAPI_TAGS.all];
  if (m.includes("content") || m.includes("terms") || m.includes("polic")) return [...STRAPI_TAGS.contents, ...STRAPI_TAGS.pages, ...STRAPI_TAGS.all];
  if (m.includes("footer")) return [...STRAPI_TAGS.footer, ...STRAPI_TAGS.all];
  if (m.includes("redirect-rule")) return [...STRAPI_TAGS.all, "strapi:redirect-rules"];
  if (m.includes("industry") || m.includes("language") || m.includes("region") || m.includes("section") || m.includes("solution") || m.includes("type")) return [...STRAPI_TAGS.misc, ...STRAPI_TAGS.all];
  return STRAPI_TAGS.all;
}

// Strapi v4/v5 webhook payloads vary: `model` may be a string
// (`api::leader.leader`), an object (`{ uid }`), or missing entirely with
// `uid` / `collection` / `contentType` at the top level. Normalize to one string.
export function modelFromWebhookBody(body) {
  if (!body || typeof body !== "object") return "";
  const direct = body.model ?? body.uid ?? body.collection ?? body.contentType ?? body.apiName;
  if (typeof direct === "string" && direct) return direct;
  if (direct && typeof direct === "object") {
    if (typeof direct.uid === "string" && direct.uid) return direct.uid;
    if (typeof direct.singularName === "string" && direct.singularName) return direct.singularName;
    if (typeof direct.collectionName === "string" && direct.collectionName) return direct.collectionName;
  }
  const entry = body.entry;
  if (entry && typeof entry === "object") {
    // Component-only saves sometimes only carry the entry shape.
    if (Array.isArray(entry.section)) {
      const comps = entry.section
        .map((s) => s?.__component)
        .filter(Boolean)
        .join(",");
      if (/leaders/i.test(comps)) return "layout.leaders";
      if (/features-elevator/i.test(comps)) return "layout.features-elevator";
      if (comps) return "content";
    }
    if (typeof entry.__component === "string" && entry.__component) return entry.__component;
  }
  return "";
}

// Buster for the persisted client cache — bump when the snapshot shape changes.
export const CLIENT_CACHE_BUSTER = "vxi-query-v1";
export const CLIENT_CACHE_KEY = "vxi-query-cache";
export const VERSION_STORAGE_KEY = "vxi-content-version";
